import { useEffect, useRef, useState } from 'react';
import {
  MEDIA_CLICKED,
  MEDIA_ENDED,
  MEDIA_ERROR,
  MEDIA_PAUSED,
  MEDIA_PLAYING,
  MEDIA_TIME_UPDATE,
  triggerEvent,
} from '@fec/assets/js/utils/event';
import { getConfig } from '@fec/assets/js/utils/config';
import useMediaProgressStorage from '@fec/frontend-aron/foundation/hooks/useMediaProgressStorage';

import Urn from '@fec/frontend/foundation/client/urn.js';

export const COMPONENTS_PRESETS = {
  DEFAULT: {
    controlBar: true,
    header: true,
    sharing: true,
    title: false,
    thumbnails: false,
    playButton: true,
    subdivisions: false,
  },
  ALL_DISABLED: {
    controlBar: false,
    header: false,
    sharing: false,
    title: false,
    thumbnails: false,
    playButton: false,
    subdivisions: false,
  },
};

const usePlayer = ({
  urn,
  frontendId,
  containerRef,
  initialize = false,
  components = COMPONENTS_PRESETS.DEFAULT,
  muted = false,
  autoplay = true,
  shouldSubscribeToTimeline = false,
  recommendations = false,
  playerFocus = false,
  calculateTimestampOffsetForEachSegment = false,
  initialSeek,
  playerA11yLabel = '',
}) => {
  const config = getConfig();
  const playerRef = useRef(null);
  const [active, setActive] = useState(false);
  const isRtr = config.BUSINESS_UNIT === 'rtr';
  const ilHost =
    config.LETTERBOX_PLAYER_IL_HOST !== ''
      ? config.LETTERBOX_PLAYER_IL_HOST
      : 'il.srgssr.ch';

  const { lastPlayerSeek } = useMediaProgressStorage({
    urn,
    shouldSubscribeToTimeline,
  });

  useEffect(() => {
    if (!initialize || !containerRef?.current) {
      return;
    }

    setActive(true);

    import('@srgssr/srgletterbox-web/dist/letterbox.css').then(() =>
      import('@srgssr/srgletterbox-web/dist/letterbox.js')
        .then((module) => {
          /* global SRGLetterbox */
          window.SRGLetterbox = module.default;
        })
        .then(() => {
          playerRef.current = new SRGLetterbox({
            debug: false,
            container: containerRef.current,
            ilHost: ilHost,
            language: isRtr ? 'rm' : 'de',
            autoplay: autoplay,
            continuousPlayback: false,
            components: components,
            muted: { default: muted, storage: false },
            textTrackLanguage: { default: isRtr ? 'de' : false, storage: true },
            textTrackSize: { default: 0, storage: true },
            recommendations: recommendations,
            playerFocus: playerFocus ? { preventScroll: true } : undefined,
            calculateTimestampOffsetForEachSegment:
              calculateTimestampOffsetForEachSegment,
          });

          // `standalone: true` makes letterbox treat sections of a show as standalone videos
          // (i.e. the time slider starts at the beginning of the section, not at the
          // position of the section within the show)
          let options = {
            standalone: true,
          };

          const pendingSeek = initialSeek ?? lastPlayerSeek ?? 0;
          if (pendingSeek) {
            options = { ...options, pendingSeek };
          }

          playerRef.current.loadUrn(urn, options);

          // set an aria-label for the player if provided
          if (playerA11yLabel !== '') {
            playerRef.current.player.el_.setAttribute(
              'aria-label',
              playerA11yLabel,
            );
          } else if (Urn.getType(urn) === 'audio') {
            // override the default aria-label of the letterbox player for audios (which is always "Video-Player")
            playerRef.current.player.el_.setAttribute(
              'aria-label',
              'Audio-Player',
            );
          }

          // Focus the player when it starts playing, this is specially important for screenreader users
          playerRef.current.on('firstplay', () =>
            playerRef.current.player.focus(),
          );

          // Inform other components when the player state changes
          playerRef.current.on('playing', () => dispatchEvent(MEDIA_PLAYING));
          playerRef.current.on('pause', () => dispatchEvent(MEDIA_PAUSED));
          playerRef.current.on('ended', () => dispatchEvent(MEDIA_ENDED));
          playerRef.current.on('error', () => dispatchEvent(MEDIA_ERROR));

          playerRef.current.on('play', () => {
            // temp workaround for analytic problems with multiple players on one page
            // see https://srgssr-ch.atlassian.net/browse/ADI-267
            // see https://srgssr-ch.atlassian.net/browse/SRFCMSAL-7965
            playerRef.current.player
              .options()
              .SRGProviders.analytics.getInternalLabels();
            // Reload the container so that internal variables are updated
            playerRef.current.player
              .options()
              .SRGProviders.analytics.loadTagCommanderContainer();
          });

          if (shouldSubscribeToTimeline) {
            playerRef.current.on('timeupdate', handleTimeUpdateEvent);
          }
        }),
    );

    return () => {
      playerRef.current?.dispose();
    };
  }, [urn, initialize, containerRef?.current, shouldSubscribeToTimeline]);

  // Handle the MEDIA_PLAYING event
  useEffect(() => {
    const handlePlayingEvent = (event) => {
      if (event.detail.frontendId !== frontendId) {
        playerRef.current?.pause();
        setActive(false);
      } else {
        setActive(true);
      }
    };

    window.addEventListener(MEDIA_PLAYING, handlePlayingEvent);
    // Once our component unmount or update, we must remove the event listener
    return () => window.removeEventListener(MEDIA_PLAYING, handlePlayingEvent);
  }, [frontendId]);

  const handleTimeUpdateEvent = () => {
    const seconds = playerRef.current.currentTime();
    let duration = playerRef.current.duration();

    // for streams, there is no duration
    if (duration === Infinity) {
      try {
        const start = playerRef.current?.player?.seekable().start(0);
        const end = playerRef.current?.player?.seekable().end(0);
        duration = end - start;
      } catch (e) {
        return;
      }
    }

    const percentage = (seconds / duration) * 100;
    // make sure percentage is always between 0 and 100
    const clampedPercentage = Math.min(Math.max(percentage, 0), 100);

    const payload = {
      urn: urn,
      seconds: seconds,
      duration: duration,
      percentage: clampedPercentage,
    };

    // we don't always know if the source is DVR when loading it
    if (
      playerRef.current?.player?.currentSource()?.streamType?.toLowerCase() ===
      'dvr'
    ) {
      // letterbox offers no obvious way to check if currentDate() can be
      // called, it just throws an error. Because we don't want to pass that
      // error on to users of this function, we catch it here.
      try {
        const currentDate = playerRef?.current?.player?.currentDate();
        if (currentDate) {
          payload.datetime = currentDate;
        }
      } catch (e) {
        // oh no
      }
    }

    triggerEvent(MEDIA_TIME_UPDATE, payload);
  };

  const dispatchEvent = (eventName) =>
    triggerEvent(eventName, { frontendId, urn });

  useEffect(() => {
    const handleMediaClicked = (event) => {
      if (!playerRef?.current) {
        return;
      }

      if (event.detail.frontendId !== frontendId) {
        // pause all other players that were not clicked
        playerRef.current.pause();
      } else if (playerRef.current.paused()) {
        // start the current player if it's paused
        playerRef.current.play();
      } else {
        // pause the current player if it's playing
        playerRef.current.pause();
      }
    };

    window.addEventListener(MEDIA_CLICKED, handleMediaClicked);

    // Once our component unmount or update, we must remove the event listener
    return () => window.removeEventListener(MEDIA_CLICKED, handleMediaClicked);
  }, [playerRef?.current, frontendId]);

  return {
    player: playerRef.current,
    playerIsActive: active,
    dispatchMediaClickedEvent: () => dispatchEvent(MEDIA_CLICKED),
  };
};

export default usePlayer;
